import { ReflectUtils } from "@dooboostore/core";
import { SwcUtils } from "../utils/Utils.js";
const SWC_APP_ROUTE_METADATA_KEY = Symbol.for("simple-web-component:swc-app-route");
const isGoConfig = (v) => !!v && typeof v === "object" && "path" in v && ("replace" in v || "state" in v || "scrollToTop" in v || typeof v.path === "object");
function createSwcAppRoute(options) {
  return (target, propertyKey, descriptor) => {
    const constructor = target.constructor;
    let list = ReflectUtils.getOwnMetadata(SWC_APP_ROUTE_METADATA_KEY, constructor);
    if (!list) {
      list = [];
      ReflectUtils.defineMetadata(SWC_APP_ROUTE_METADATA_KEY, list, constructor);
    }
    list.push({ propertyKey, options });
    if (!descriptor) {
      return descriptor;
    }
    const originalMethod = descriptor.value;
    descriptor.value = function(...args) {
      const result = originalMethod.apply(this, args);
      const extractValue = (v) => {
        const keyToUse = options?.valueKey ?? SWC_APP_ROUTE_METADATA_KEY;
        if (v && typeof v === "object" && keyToUse in v) {
          return v[keyToUse];
        }
        return v;
      };
      const route = (value) => {
        if (value === void 0 || value === null || value === false) return;
        const router = SwcUtils.getAppHost(this)?.router;
        if (!router) {
          console.warn("swcAppRoute: no SwcApp router found for", this);
          return;
        }
        if (options?.filter) {
          const helper = SwcUtils.getHelperAndHostSet(this);
          if (!options.filter(router, value, { currentThis: this, helper })) return;
        }
        const type = options?.type ?? "go";
        if (type === "go") {
          if (typeof value === "number") return router.go(value);
          const defaults = { replace: options?.replace, state: options?.state, scrollToTop: options?.scrollToTop };
          return router.go(isGoConfig(value) ? { ...defaults, ...value } : { ...defaults, path: value });
        }
        const state = options?.state;
        if ((type === "push" || type === "replace") && router.hasLeaveGuards) {
          return router.canLeave(router.getRouteData({ pathOrUrl: router.toUrl(value) }), router.value).then((ok) => {
            if (!ok) return;
            if (type === "push") router.push(value, state);
            else router.replace(value, state);
          });
        }
        switch (type) {
          case "push":
            return router.push(value, state);
          case "replace":
            return router.replace(value, state);
          case "pushDeleteSearchParam":
            return router.pushDeleteSearchParam(value, state);
          case "pushDeleteHashSearchParam":
            return router.pushDeleteHashSearchParam(value, state);
          case "pushAddSearchParam":
            return router.pushAddSearchParam(value, state);
          case "pushUpsertSearchParam":
            return router.pushUpsertSearchParam(value, state);
          case "replaceDeleteSearchParam":
            return router.replaceDeleteSearchParam(value, state);
          case "replaceDeleteHashSearchParam":
            return router.replaceDeleteHashSearchParam(value, state);
          case "replaceAddSearchParam":
            return router.replaceAddSearchParam(value, state);
          case "replaceUpsertSearchParam":
            return router.replaceUpsertSearchParam(value, state);
        }
      };
      if (result instanceof Promise) {
        result.then((data) => route(extractValue(data))).catch((err) => {
          console.warn("swcAppRoute async error:", err);
        });
      } else {
        route(extractValue(result));
      }
      return result;
    };
    return descriptor;
  };
}
function swcAppRoute(targetOrOptions, propertyKey, descriptor) {
  if (targetOrOptions && typeof targetOrOptions === "object" && propertyKey && (typeof propertyKey === "string" || typeof propertyKey === "symbol") && descriptor) {
    return createSwcAppRoute()(targetOrOptions, propertyKey, descriptor);
  }
  return createSwcAppRoute(targetOrOptions);
}
const typedRoute = (type) => (targetOrOptions, propertyKey, descriptor) => {
  if (targetOrOptions && typeof targetOrOptions === "object" && propertyKey && (typeof propertyKey === "string" || typeof propertyKey === "symbol") && descriptor) {
    return createSwcAppRoute({ type })(targetOrOptions, propertyKey, descriptor);
  }
  return createSwcAppRoute({ ...targetOrOptions ?? {}, type });
};
const swcAppRouteGo = typedRoute("go");
const swcAppRoutePush = typedRoute("push");
const swcAppRouteReplace = typedRoute("replace");
const swcAppRoutePushDeleteSearchParam = typedRoute("pushDeleteSearchParam");
const swcAppRoutePushDeleteHashSearchParam = typedRoute("pushDeleteHashSearchParam");
const swcAppRoutePushAddSearchParam = typedRoute("pushAddSearchParam");
const swcAppRoutePushUpsertSearchParam = typedRoute("pushUpsertSearchParam");
const swcAppRouteReplaceDeleteSearchParam = typedRoute("replaceDeleteSearchParam");
const swcAppRouteReplaceDeleteHashSearchParam = typedRoute("replaceDeleteHashSearchParam");
const swcAppRouteReplaceAddSearchParam = typedRoute("replaceAddSearchParam");
const swcAppRouteReplaceUpsertSearchParam = typedRoute("replaceUpsertSearchParam");
const getSwcAppRouteMetadata = (target) => {
  const constructor = typeof target === "function" ? target : target.constructor;
  return ReflectUtils.findAllMetadata(SWC_APP_ROUTE_METADATA_KEY, constructor).flat();
};
export {
  SWC_APP_ROUTE_METADATA_KEY,
  getSwcAppRouteMetadata,
  swcAppRoute,
  swcAppRouteGo,
  swcAppRoutePush,
  swcAppRoutePushAddSearchParam,
  swcAppRoutePushDeleteHashSearchParam,
  swcAppRoutePushDeleteSearchParam,
  swcAppRoutePushUpsertSearchParam,
  swcAppRouteReplace,
  swcAppRouteReplaceAddSearchParam,
  swcAppRouteReplaceDeleteHashSearchParam,
  swcAppRouteReplaceDeleteSearchParam,
  swcAppRouteReplaceUpsertSearchParam
};
//# sourceMappingURL=swcAppRoute.js.map
